import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Calendar, TrendingUp, CheckCircle, XCircle, Mic, FileText, Youtube, Instagram } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import VideoEmbed from '../components/VideoEmbed';

const Admin = () => {
  const [stats, setStats] = useState({});
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    banner_url: '',
  });
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    category: '',
    file_url: '',
    file_name: '',
    file_type: '',
    external_link: '',
  });
  const [ommVideos, setOmmVideos] = useState([]);
  const [ommForm, setOmmForm] = useState({
    title: '',
    description: '',
    embed_code: '',
  });
  const [brochures, setBrochures] = useState([]);
  const [brochureForm, setBrochureForm] = useState({
    title: '',
    description: '',
    file_url: '',
    file_name: '',
  });
  const [brochureUploading, setBrochureUploading] = useState(false);
  const [shorts, setShorts] = useState([]);
  const [shortForm, setShortForm] = useState({ title: '', youtube_url: '' });
  const [instaPosts, setInstaPosts] = useState([]);
  const [instaForm, setInstaForm] = useState({ instagram_url: '', caption: '', share_on_feed: true });

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, pendingRes, resourcesRes, approvedUsersRes, postsRes, ommRes, brochuresRes, shortsRes, instaRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/pending-users'),
        api.get('/resources'),
        api.get('/users/directory'),
        api.get('/posts'),
        api.get('/omm-videos'),
        api.get('/brochures'),
        api.get('/youtube-shorts'),
        api.get('/instagram-posts'),
      ]);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data);
      setResources(resourcesRes.data);
      setApprovedUsers(approvedUsersRes.data);
      setAllPosts(postsRes.data);
      setOmmVideos(ommRes.data);
      setBrochures(brochuresRes.data);
      setShorts(shortsRes.data);
      setInstaPosts(instaRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleBrochureFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    setBrochureUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setBrochureForm({
        ...brochureForm,
        file_url: reader.result,
        file_name: file.name,
      });
      setBrochureUploading(false);
      toast.success(`Selected: ${file.name}`);
    };
    reader.onerror = () => {
      setBrochureUploading(false);
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleCreateBrochure = async () => {
    if (!brochureForm.title.trim() || !brochureForm.file_url) {
      toast.error('Title and PDF file are required');
      return;
    }
    try {
      await api.post('/admin/brochures', brochureForm);
      toast.success('Brochure added');
      setBrochureForm({ title: '', description: '', file_url: '', file_name: '' });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add brochure');
    }
  };

  const handleDeleteBrochure = async (id) => {
    if (!window.confirm('Delete this brochure?')) return;
    try {
      await api.delete(`/admin/brochures/${id}`);
      toast.success('Brochure deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete brochure');
    }
  };

  const handleCreateOmmVideo = async () => {
    if (!ommForm.title.trim() || !ommForm.embed_code.trim()) {
      toast.error('Title and embed code/URL are required');
      return;
    }
    try {
      await api.post('/admin/omm-videos', ommForm);
      toast.success('Video added');
      setOmmForm({ title: '', description: '', embed_code: '' });
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to add video');
    }
  };

  const handleDeleteOmmVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.delete(`/admin/omm-videos/${id}`);
      toast.success('Video deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleCreateShort = async () => {
    if (!shortForm.title.trim() || !shortForm.youtube_url.trim()) {
      toast.error('Title and YouTube URL are required');
      return;
    }
    try {
      await api.post('/admin/youtube-shorts', shortForm);
      toast.success('Short added');
      setShortForm({ title: '', youtube_url: '' });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add short');
    }
  };

  const handleDeleteShort = async (id) => {
    if (!window.confirm('Delete this short?')) return;
    try {
      await api.delete(`/admin/youtube-shorts/${id}`);
      toast.success('Short deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete short');
    }
  };

  const handleCreateInsta = async () => {
    if (!instaForm.instagram_url.trim()) {
      toast.error('Instagram URL is required');
      return;
    }
    try {
      await api.post('/admin/instagram-posts', instaForm);
      toast.success(instaForm.share_on_feed ? 'Added to Instagram section and Feed' : 'Instagram post added');
      setInstaForm({ instagram_url: '', caption: '', share_on_feed: true });
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add post');
    }
  };

  const handleDeleteInsta = async (id) => {
    if (!window.confirm('Delete this Instagram post?')) return;
    try {
      await api.delete(`/admin/instagram-posts/${id}`);
      toast.success('Post deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.post(`/admin/approve-user/${userId}`);
      toast.success('User approved');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Reject this user? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User rejected');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const handleCreateEvent = async () => {
    try {
      await api.post('/admin/events', eventForm);
      toast.success('Event created');
      setEventForm({
        title: '',
        description: '',
        date: '',
        location: '',
        banner_url: '',
      });
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleCreateResource = async () => {
    try {
      await api.post('/admin/resources', resourceForm);
      toast.success('Resource created');
      setResourceForm({
        title: '',
        description: '',
        category: '',
        file_url: '',
        file_name: '',
        file_type: '',
        external_link: '',
      });
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to create resource');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Delete this resource?')) return;
    
    try {
      await api.delete(`/admin/resources/${resourceId}`);
      toast.success('Resource deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const handleDeleteApprovedUser = async (userId) => {
    if (!window.confirm('Permanently delete this user? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    const newPassword = prompt(`Enter new temporary password for ${userName}:`);
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${userName}. New password: ${newPassword}`);
      alert(`Password reset successfully!\n\nUser: ${userName}\nNew Password: ${newPassword}\n\nPlease share this password with the user securely.`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/posts/${postId}`);
      toast.success('Post deleted');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventForm({ ...eventForm, banner_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setResourceForm({
          ...resourceForm,
          file_url: reader.result,
          file_name: file.name,
          file_type: file.type,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight mb-2">
            Admin Dashboard
          </h1>
          <p className="text-slate-600">Manage GBA Connect community</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="stats-total-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Total Users</p>
                <p className="text-3xl font-bold text-[#1E3A8A] mt-1">{stats.total_users || 0}</p>
              </div>
              <Users className="h-10 w-10 text-slate-300" />
            </div>
          </Card>

          <Card className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="stats-approved-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Approved</p>
                <p className="text-3xl font-bold text-[#22C55E] mt-1">{stats.approved_users || 0}</p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-200" />
            </div>
          </Card>

          <Card className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="stats-pending-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Pending</p>
                <p className="text-3xl font-bold text-yellow-600 mt-1">{stats.pending_users || 0}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-yellow-200" />
            </div>
          </Card>

          <Card className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="stats-total-posts">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide">Total Posts</p>
                <p className="text-3xl font-bold text-[#1E3A8A] mt-1">{stats.total_posts || 0}</p>
              </div>
              <Calendar className="h-10 w-10 text-slate-300" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-white !flex !flex-wrap h-auto w-full justify-start gap-2">
            <TabsTrigger value="pending" data-testid="tab-pending-users">
              Pending Approvals ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-manage-users">
              Manage Users ({approvedUsers.length})
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-manage-posts">
              Manage Posts ({allPosts.length})
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-create-event">Create Event</TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources">Resources ({resources.length})</TabsTrigger>
            <TabsTrigger value="omm" data-testid="tab-omm">One Minute Mentor ({ommVideos.length})</TabsTrigger>
            <TabsTrigger value="brochures" data-testid="tab-brochures">Brochures ({brochures.length})</TabsTrigger>
            <TabsTrigger value="shorts" data-testid="tab-shorts">YouTube ({shorts.length})</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="tab-instagram">Instagram ({instaPosts.length})</TabsTrigger>
          </TabsList>

          {/* Pending Users */}
          <TabsContent value="pending" data-testid="pending-users-container">
            {pendingUsers.length === 0 ? (
              <Card className="p-12 text-center bg-white">
                <p className="text-slate-500">No pending approvals</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map((user) => (
                  <Card key={user.id} className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="pending-user-card">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                          <AvatarImage src={user.profile_photo} />
                          <AvatarFallback className="bg-[#1E3A8A] text-white">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{user.name}</h3>
                          <p className="text-sm text-slate-600">{user.email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge>GBA Alumni</Badge>
                            {user.batch && <Badge variant="outline">{user.batch}</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApproveUser(user.id)}
                          className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                          data-testid="approve-user-button"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleRejectUser(user.id)}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          data-testid="reject-user-button"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                    {user.bio && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <p className="text-sm text-slate-700">{user.bio}</p>
                      </div>
                    )}
                    {user.gba_journey && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">GBA Journey</p>
                        <p className="text-sm text-slate-700">{user.gba_journey}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Manage Approved Users */}
          <TabsContent value="users" data-testid="manage-users-container">
            {approvedUsers.length === 0 ? (
              <Card className="p-12 text-center bg-white">
                <p className="text-slate-500">No approved users yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {approvedUsers.map((user) => (
                  <Card key={user.id} className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="approved-user-card">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                          <AvatarImage src={user.profile_photo} />
                          <AvatarFallback className="bg-[#1E3A8A] text-white">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{user.name}</h3>
                          <p className="text-sm text-slate-600">{user.email}</p>
                          {user.phone && (
                            <p className="text-sm text-slate-600">📱 {user.phone}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge>GBA Alumni</Badge>
                            {user.batch && <Badge variant="outline">{user.batch}</Badge>}
                            {user.current_role && (
                              <span className="text-xs text-slate-500">• {user.current_role}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleResetPassword(user.id, user.name)}
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50 border-blue-200"
                          data-testid="reset-password-button"
                        >
                          Reset Password
                        </Button>
                        <Button
                          onClick={() => handleDeleteApprovedUser(user.id)}
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 border-red-200"
                          data-testid="delete-user-button"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Delete User
                        </Button>
                      </div>
                    </div>
                    {user.bio && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <p className="text-sm text-slate-700">{user.bio}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Manage Posts */}
          <TabsContent value="posts" data-testid="manage-posts-container">
            {allPosts.length === 0 ? (
              <Card className="p-12 text-center bg-white">
                <p className="text-slate-500">No posts yet</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {allPosts.map((post) => (
                  <Card key={post.id} className="p-6 bg-white border border-slate-100 shadow-sm" data-testid="admin-post-card">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={post.author_photo} />
                            <AvatarFallback className="bg-[#1E3A8A] text-white text-sm">
                              {getInitials(post.author_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-sm text-slate-900">{post.author_name}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(post.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="mb-3">
                          <Badge className={
                            post.type === 'win' ? 'bg-green-100 text-green-700' :
                            post.type === 'question' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {post.type.toUpperCase()}
                          </Badge>
                          {post.is_featured && (
                            <Badge className="ml-2 bg-yellow-100 text-yellow-700">
                              FEATURED
                            </Badge>
                          )}
                        </div>

                        <p className="text-sm text-slate-700 mb-3 line-clamp-3">
                          {post.content}
                        </p>

                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>❤️ {post.likes?.length || 0} likes</span>
                          <span>✨ {post.celebrates?.length || 0} celebrates</span>
                          <span>💬 {post.comments?.length || 0} comments</span>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleDeletePost(post.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50 border-red-200"
                        data-testid="admin-delete-post-button"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Create Event */}
          <TabsContent value="events" data-testid="create-event-container">
            <Card className="p-6 bg-white border border-slate-100 shadow-sm">
              <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                Create New Event
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="e.g., GBA Annual Reunion 2024"
                    data-testid="event-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Event details..."
                    rows={4}
                    data-testid="event-description-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="datetime-local"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                      data-testid="event-date-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      placeholder="e.g., Mumbai, India"
                      data-testid="event-location-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="banner">Event Banner</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="banner-upload"
                  />
                  <label htmlFor="banner-upload">
                    <Button variant="outline" className="w-full" asChild>
                      <span>Upload Banner Image</span>
                    </Button>
                  </label>
                  {eventForm.banner_url && (
                    <img
                      src={eventForm.banner_url}
                      alt="Banner preview"
                      className="mt-2 rounded-md max-h-40 object-cover"
                    />
                  )}
                </div>

                <Button
                  onClick={handleCreateEvent}
                  disabled={!eventForm.title || !eventForm.description || !eventForm.date}
                  className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold"
                  data-testid="create-event-button"
                >
                  Create Event
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Resources Management */}
          <TabsContent value="resources" data-testid="resources-management-container">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Create Resource Form */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                  Upload New Resource
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="resource-title">Title *</Label>
                    <Input
                      id="resource-title"
                      value={resourceForm.title}
                      onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                      placeholder="e.g., Sales Objection Handling Guide"
                      data-testid="resource-title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="resource-description">Description *</Label>
                    <Textarea
                      id="resource-description"
                      value={resourceForm.description}
                      onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                      placeholder="Brief description of the resource..."
                      rows={3}
                      data-testid="resource-description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="resource-category">Category *</Label>
                    <Input
                      id="resource-category"
                      value={resourceForm.category}
                      onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })}
                      placeholder="e.g., Sales Scripts, Training Materials, Templates"
                      data-testid="resource-category-input"
                    />
                  </div>

                  <div>
                    <Label>Upload File (optional)</Label>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload">
                      <Button variant="outline" className="w-full" asChild>
                        <span>Choose File</span>
                      </Button>
                    </label>
                    {resourceForm.file_name && (
                      <p className="text-sm text-green-600 mt-2">✓ {resourceForm.file_name}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="resource-link">External Link (optional)</Label>
                    <Input
                      id="resource-link"
                      value={resourceForm.external_link}
                      onChange={(e) => setResourceForm({ ...resourceForm, external_link: e.target.value })}
                      placeholder="https://example.com/resource"
                      data-testid="resource-link-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Provide either a file upload or external link
                    </p>
                  </div>

                  <Button
                    onClick={handleCreateResource}
                    disabled={!resourceForm.title || !resourceForm.description || !resourceForm.category || (!resourceForm.file_url && !resourceForm.external_link)}
                    className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold"
                    data-testid="create-resource-button"
                  >
                    Upload Resource
                  </Button>
                </div>
              </Card>

              {/* Resources List */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                  Manage Resources
                </h2>
                {resources.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No resources uploaded yet</p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {resources.map((resource) => (
                      <Card key={resource.id} className="p-4 border border-slate-200" data-testid="resource-item">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm text-slate-900 mb-1 line-clamp-1">
                              {resource.title}
                            </h4>
                            <Badge className="text-xs mb-2">{resource.category}</Badge>
                            <p className="text-xs text-slate-600 line-clamp-2">{resource.description}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              {resource.downloads} downloads
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteResource(resource.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-resource-button"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* One Minute Mentor Management */}
          <TabsContent value="omm" data-testid="omm-management-container">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload form */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4 flex items-center gap-2">
                  <Mic className="h-5 w-5" /> Add One Minute Mentor Video
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="omm-title">Title *</Label>
                    <Input
                      id="omm-title"
                      value={ommForm.title}
                      onChange={(e) => setOmmForm({ ...ommForm, title: e.target.value })}
                      placeholder="e.g., Lesson 1: Handling Rejection"
                      data-testid="omm-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="omm-description">Description</Label>
                    <Textarea
                      id="omm-description"
                      value={ommForm.description}
                      onChange={(e) => setOmmForm({ ...ommForm, description: e.target.value })}
                      placeholder="Short summary of the lesson..."
                      rows={2}
                      data-testid="omm-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="omm-embed">YouTube URL or Embed Code *</Label>
                    <Textarea
                      id="omm-embed"
                      value={ommForm.embed_code}
                      onChange={(e) => setOmmForm({ ...ommForm, embed_code: e.target.value })}
                      placeholder='Paste a YouTube link (https://youtu.be/...) or full <iframe ...></iframe> embed code'
                      rows={4}
                      className="font-mono text-xs"
                      data-testid="omm-embed-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Accepts YouTube URLs, video IDs, or any <code>&lt;iframe&gt;</code> embed (Vimeo, Loom, Wistia, etc.)
                    </p>
                  </div>
                  <Button
                    onClick={handleCreateOmmVideo}
                    disabled={!ommForm.title || !ommForm.embed_code}
                    className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold"
                    data-testid="create-omm-button"
                  >
                    Add Video
                  </Button>
                </div>
              </Card>

              {/* Existing videos */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                  Manage Videos
                </h2>
                {ommVideos.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No videos uploaded yet</p>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {ommVideos.map((v) => (
                      <Card key={v.id} className="p-4 border border-slate-200" data-testid="omm-admin-item">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm text-slate-900 mb-1 line-clamp-1">{v.title}</h4>
                            {v.description && (
                              <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed line-clamp-3">{v.description}</p>
                            )}
                            <p className="text-xs text-slate-500 mt-1">{v.views || 0} views</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteOmmVideo(v.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-omm-button"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                        <VideoEmbed html={v.embed_code} />
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Brochures Management */}
          <TabsContent value="brochures" data-testid="brochures-management-container">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload form */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5" /> Upload Brochure
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="brochure-title">Title *</Label>
                    <Input
                      id="brochure-title"
                      value={brochureForm.title}
                      onChange={(e) => setBrochureForm({ ...brochureForm, title: e.target.value })}
                      placeholder="e.g., GBA Sales Mastery Brochure 2026"
                      data-testid="brochure-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="brochure-description">Description</Label>
                    <Textarea
                      id="brochure-description"
                      value={brochureForm.description}
                      onChange={(e) => setBrochureForm({ ...brochureForm, description: e.target.value })}
                      placeholder="Short summary of what's inside this brochure..."
                      rows={3}
                      data-testid="brochure-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="brochure-file">PDF File * (max 10MB)</Label>
                    <Input
                      id="brochure-file"
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handleBrochureFile}
                      className="cursor-pointer"
                      data-testid="brochure-file-input"
                    />
                    {brochureForm.file_name && (
                      <p className="text-xs text-[#22C55E] mt-1 font-medium">
                        ✓ {brochureForm.file_name}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleCreateBrochure}
                    disabled={brochureUploading || !brochureForm.title || !brochureForm.file_url}
                    className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold"
                    data-testid="create-brochure-button"
                  >
                    {brochureUploading ? 'Reading file...' : 'Add Brochure'}
                  </Button>
                </div>
              </Card>

              {/* Existing brochures */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                  Manage Brochures
                </h2>
                {brochures.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No brochures uploaded yet</p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {brochures.map((b) => (
                      <Card key={b.id} className="p-4 border border-slate-200" data-testid="brochure-admin-item">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-red-50 rounded">
                              <FileText className="h-5 w-5 text-red-600" />
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-semibold text-sm text-slate-900 line-clamp-1">{b.title}</h4>
                              {b.description && (
                                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{b.description}</p>
                              )}
                              <p className="text-xs text-slate-500 mt-1">
                                {b.file_name || 'PDF'} • {b.downloads || 0} downloads
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteBrochure(b.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-brochure-button"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* YouTube Shorts Management */}
          <TabsContent value="shorts" data-testid="shorts-management-container">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload form */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4 flex items-center gap-2">
                  <Youtube className="h-5 w-5 text-red-600" /> Add YouTube Short
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="short-title">Title *</Label>
                    <Input
                      id="short-title"
                      value={shortForm.title}
                      onChange={(e) => setShortForm({ ...shortForm, title: e.target.value })}
                      placeholder="e.g., 10X Your Sales Even in a Slow Market"
                      data-testid="short-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="short-url">YouTube Shorts URL or Video ID *</Label>
                    <Input
                      id="short-url"
                      value={shortForm.youtube_url}
                      onChange={(e) => setShortForm({ ...shortForm, youtube_url: e.target.value })}
                      placeholder="https://www.youtube.com/shorts/VIDEO_ID"
                      data-testid="short-url-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Accepts shorts URLs, watch URLs, youtu.be links, or plain 11-char video IDs.
                    </p>
                  </div>
                  <Button
                    onClick={handleCreateShort}
                    disabled={!shortForm.title || !shortForm.youtube_url}
                    className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold"
                    data-testid="create-short-button"
                  >
                    Add Short
                  </Button>
                </div>
              </Card>

              {/* Existing shorts */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                  Manage Shorts
                </h2>
                {shorts.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No shorts added yet</p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {shorts.map((s) => (
                      <Card key={s.id} className="p-3 border border-slate-200" data-testid="short-admin-item">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <img
                              src={`https://i.ytimg.com/vi/${s.video_id}/mqdefault.jpg`}
                              alt={s.title}
                              className="w-16 h-24 object-cover rounded shrink-0 bg-slate-200"
                            />
                            <div className="min-w-0">
                              <h4 className="font-semibold text-sm text-slate-900 line-clamp-2">{s.title}</h4>
                              <p className="text-xs text-slate-500 mt-1">{s.views || 0} views</p>
                              <a
                                href={s.youtube_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-red-600 hover:underline"
                              >
                                Open on YouTube
                              </a>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteShort(s.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-short-button"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Instagram Management */}
          <TabsContent value="instagram" data-testid="instagram-management-container">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload form */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4 flex items-center gap-2">
                  <Instagram className="h-5 w-5 text-pink-600" /> Add Instagram Post or Reel
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="insta-url">Instagram URL *</Label>
                    <Input
                      id="insta-url"
                      value={instaForm.instagram_url}
                      onChange={(e) => setInstaForm({ ...instaForm, instagram_url: e.target.value })}
                      placeholder="https://www.instagram.com/p/... or /reel/..."
                      data-testid="insta-url-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Accepts /p/CODE/ (posts) or /reel/CODE/ (reels). Post must be public.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="insta-caption">Caption (optional)</Label>
                    <Textarea
                      id="insta-caption"
                      value={instaForm.caption}
                      onChange={(e) => setInstaForm({ ...instaForm, caption: e.target.value })}
                      placeholder="Short note about this post (optional — Instagram's own caption shows in the embed)"
                      rows={2}
                      data-testid="insta-caption-input"
                    />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <input
                      type="checkbox"
                      checked={instaForm.share_on_feed}
                      onChange={(e) => setInstaForm({ ...instaForm, share_on_feed: e.target.checked })}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#22C55E] focus:ring-[#22C55E]"
                      data-testid="insta-share-feed-checkbox"
                    />
                    <span className="text-sm text-slate-700">
                      <span className="font-semibold">Also share on Feed</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Creates a Feed post with this reel/post embedded so members see it on the homepage.</span>
                    </span>
                  </label>
                  <Button
                    onClick={handleCreateInsta}
                    disabled={!instaForm.instagram_url}
                    className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold"
                    data-testid="create-insta-button"
                  >
                    Add Post
                  </Button>
                </div>
              </Card>

              {/* Existing posts */}
              <Card className="p-6 bg-white border border-slate-100 shadow-sm">
                <h2 className="font-oswald text-xl font-bold text-[#1E3A8A] uppercase mb-4">
                  Manage Posts &amp; Reels
                </h2>
                {instaPosts.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No Instagram posts added yet</p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {instaPosts.map((p) => (
                      <Card key={p.id} className="p-3 border border-slate-200" data-testid="insta-admin-item">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-pink-50 rounded shrink-0">
                              <Instagram className="h-5 w-5 text-pink-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={p.kind === 'reel' ? 'bg-pink-100 text-pink-700' : 'bg-purple-100 text-purple-700'}>
                                  {p.kind === 'reel' ? 'Reel' : 'Post'}
                                </Badge>
                                <code className="text-xs text-slate-500">{p.shortcode}</code>
                              </div>
                              <a
                                href={p.instagram_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-pink-600 hover:underline break-all"
                              >
                                {p.instagram_url}
                              </a>
                              <p className="text-xs text-slate-500 mt-1">{p.views || 0} views</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteInsta(p.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid="delete-insta-button"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
